import React, { useState, useEffect } from 'react';
import { AdminApiClient } from '../services/adminApiClient';
import { 
  ShoppingBag, 
  Search, 
  Filter, 
  Flag, 
  Sparkles, 
  EyeOff, 
  Trash2, 
  Leaf, 
  Tag, 
  ExternalLink, 
  Plus, 
  Edit2, 
  Layers, 
  Smartphone, 
  Check, 
  AlertTriangle,
  Eye,
  ArrowUpCircle,
  Download,
  FileSpreadsheet,
  Users,
  UserCheck,
  UserX,
  Ban,
  ShieldAlert,
  CheckCircle2,
  RotateCcw,
  MessageSquare,
  Clock,
  Star,
  ShieldCheck,
  HelpCircle,
  X,
  ChevronRight,
  AlertCircle,
  Share2,
  CheckCircle
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { 
  CatalogListing, 
  MarketplaceCategory, 
  UserRecord, 
  UserReportIncident,
  UserBanInfo,
  UserAppealInfo 
} from '../types/admin';

export const ListingsView: React.FC = () => {
  const [tab, setTab] = useState<'all' | 'flagged' | 'delisted' | 'categories' | 'participants'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Participants Tab State
  const [participantRoleFilter, setParticipantRoleFilter] = useState<'all' | 'vendors' | 'buyers' | 'reported' | 'banned' | 'appeals'>('all');
  const [participantSearch, setParticipantSearch] = useState('');

  // Notifications / Toast
  const [actionToast, setActionToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setActionToast(msg);
    setTimeout(() => setActionToast(null), 3500);
  };

  // =========================================================================
  // 1. SEED LISTINGS DATA
  // =========================================================================
  const [listings, setListings] = useState<CatalogListing[]>([
    {
      id: 'lst_phone_092',
      title: 'iPhone 12 Pro 128GB (Pacific Blue)',
      description: 'Used for 1 year, pristine condition with battery health at 88%. Comes with original USB-C cable and shockproof case. Escrow verified.',
      category: 'Electronics',
      price: 350000,
      condition: 'Like New',
      ecoScore: 92,
      co2SavingsKg: 24.5,
      sellerId: 'usr_002_tunde',
      sellerName: 'Tunde Bakare',
      sellerRating: 4.9,
      imageUrls: [
        'https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'active',
      isFeatured: true,
      aiModerationStatus: 'clean',
      aiConfidenceScore: 99.2,
      location: 'Lekki Phase 1, Lagos',
      createdAt: '2 days ago'
    },
    {
      id: 'lst_furn_110',
      title: 'Vintage Mid-Century Teak Armchair',
      description: 'Solid teak wood frame, freshly reupholstered in green linen. Extremely sturdy, reclaimed from antique warehouse in Yaba.',
      category: 'Furniture',
      price: 65000,
      condition: 'Good',
      ecoScore: 96,
      co2SavingsKg: 42.0,
      sellerId: 'usr_002_tunde',
      sellerName: 'Tunde Bakare',
      sellerRating: 4.9,
      imageUrls: [
        'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'active',
      isFeatured: false,
      aiModerationStatus: 'clean',
      aiConfidenceScore: 98.7,
      location: 'Ikeja GRA, Lagos',
      createdAt: '3 days ago'
    },
    {
      id: 'lst_suspicious_099',
      title: 'Replica Rolex Submariner Automatic Watch',
      description: 'High grade 1:1 copy Rolex watch, sweeping second hand, identical weight with sapphire glass.',
      category: 'Fashion',
      price: 45000,
      condition: 'Brand New',
      ecoScore: 40,
      co2SavingsKg: 5.0,
      sellerId: 'usr_005_emeka',
      sellerName: 'Emeka Johnson',
      sellerRating: 3.2,
      imageUrls: [
        'https://images.unsplash.com/photo-1524805444758-089113d48a6d?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'flagged',
      flagReason: 'Counterfeit / Replica goods prohibited under ReUseIt Trust Guidelines',
      aiModerationStatus: 'quarantined',
      aiConfidenceScore: 96.5,
      isFeatured: false,
      location: 'Yaba, Lagos',
      createdAt: 'Yesterday'
    },
    {
      id: 'lst_delisted_077',
      title: 'PlayStation 4 Pro 1TB with 2 DualShock Controllers',
      description: 'Includes FIFA 24 and God of War Ragnarok discs. Console has minor surface scratches but runs whisper quiet.',
      category: 'Electronics',
      price: 180000,
      condition: 'Good',
      ecoScore: 84,
      co2SavingsKg: 19.5,
      sellerId: 'usr_001_alex',
      sellerName: 'Alex Okafor',
      sellerRating: 4.8,
      imageUrls: [
        'https://images.unsplash.com/photo-1507457379470-08b800bebc67?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'delisted',
      delistedReason: 'Temporarily delisted by moderator pending serial number verification',
      delistedAt: '3 days ago',
      aiModerationStatus: 'clean',
      aiConfidenceScore: 95.0,
      isFeatured: false,
      location: 'Victoria Island, Lagos',
      createdAt: '1 week ago'
    },
    {
      id: 'lst_macbook_042',
      title: 'MacBook Air M1 256GB Silver',
      description: 'Lightly used for university coursework, 100% functional, 23 battery cycles. Includes original box and 30W adapter.',
      category: 'Electronics',
      price: 480000,
      condition: 'Like New',
      ecoScore: 89,
      co2SavingsKg: 35.0,
      sellerId: 'usr_001_alex',
      sellerName: 'Alex Okafor',
      sellerRating: 4.8,
      imageUrls: [
        'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'sold',
      aiModerationStatus: 'clean',
      aiConfidenceScore: 99.0,
      isFeatured: false,
      location: 'Victoria Island, Lagos',
      createdAt: '5 days ago'
    },
    {
      id: 'lst_bike_055',
      title: 'Giant Escape 3 Commuter Road Bicycle',
      description: 'Aluminum frame, 21-speed Shimano gears, puncture-resistant tires. Perfect for green eco-commuting in Lagos.',
      category: 'Vehicles',
      price: 120000,
      condition: 'Good',
      ecoScore: 98,
      co2SavingsKg: 78.0,
      sellerId: 'usr_007_chidi',
      sellerName: 'Chidi Nnamdi',
      sellerRating: 4.7,
      imageUrls: [
        'https://images.unsplash.com/photo-1485965120184-e220f721d03e?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'active',
      isFeatured: true,
      aiModerationStatus: 'clean',
      aiConfidenceScore: 97.8,
      location: 'Surulere, Lagos',
      createdAt: '4 days ago'
    }
  ]);

  useEffect(() => {
    AdminApiClient.getListings()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: CatalogListing[] = data.map((l: any) => ({
            id: l.id,
            title: l.title,
            description: l.description,
            category: l.category?.name || 'General',
            price: parseFloat(l.price || '0'),
            condition: l.condition || 'Good',
            ecoScore: l.ecoScore || 85,
            co2SavingsKg: l.co2SavedKg || 15.0,
            sellerId: l.sellerId,
            sellerName: l.seller?.displayName || 'Verified Seller',
            sellerRating: 4.9,
            imageUrls: l.images && l.images.length > 0 ? l.images : ['https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600'],
            status: l.status?.toLowerCase() || 'active',
            isFeatured: l.boostTier === 'SPOTLIGHT' || l.boostTier === 'ELITE',
            aiModerationStatus: 'clean',
            aiConfidenceScore: 98.5,
            location: `${l.locationCity || 'Lagos'}, ${l.locationState || 'Nigeria'}`,
            createdAt: new Date(l.createdAt).toLocaleDateString(),
          }));
          setListings(mapped);
        }
      })
      .catch((err) => console.error('Failed to load listings from API:', err));
  }, []);

  // =========================================================================
  // 2. SEED PARTICIPANTS DATA (VENDORS & BUYERS)
  // =========================================================================
  const [participants, setParticipants] = useState<UserRecord[]>([
    {
      uid: 'usr_001_alex',
      name: 'Alex Okafor',
      email: 'alex.okafor@gmail.com',
      phone: '+234 803 123 4567',
      avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=faces',
      role: 'both',
      status: 'active',
      tier: 'tier2_silver',
      sellerTrustScore: 92,
      walletBalance: 48500,
      carbonOffsetKg: 84.5,
      tradesCompleted: 16,
      activeListingsCount: 1,
      ordersCount: 9,
      totalSpent: 310000,
      totalSalesVolume: 660000,
      kycStatus: 'verified',
      reportCount: 0,
      createdAt: '2025-11-10'
    },
    {
      uid: 'usr_002_tunde',
      name: 'Tunde Bakare',
      email: 'tunde.bakare@yahoo.com',
      phone: '+234 802 987 6543',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=160&h=160&fit=crop&crop=faces',
      role: 'vendor',
      status: 'active',
      tier: 'tier3_champion',
      sellerTrustScore: 98,
      walletBalance: 142000,
      carbonOffsetKg: 312.0,
      tradesCompleted: 58,
      activeListingsCount: 2,
      ordersCount: 3,
      totalSpent: 45000,
      totalSalesVolume: 2450000,
      kycStatus: 'verified',
      reportCount: 0,
      createdAt: '2025-08-14'
    },
    {
      uid: 'usr_003_chioma',
      name: 'Chioma Eze',
      email: 'chioma.eze@hotmail.com',
      phone: '+234 814 555 1212',
      avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=160&h=160&fit=crop&crop=faces',
      role: 'buyer',
      status: 'active',
      tier: 'tier1_bronze',
      sellerTrustScore: 80,
      walletBalance: 12000,
      carbonOffsetKg: 18.2,
      tradesCompleted: 4,
      activeListingsCount: 0,
      ordersCount: 12,
      totalSpent: 490000,
      totalSalesVolume: 0,
      kycStatus: 'verified',
      reportCount: 0,
      createdAt: '2026-01-05'
    },
    {
      uid: 'usr_004_fatima',
      name: 'Fatima Sani',
      email: 'fatima.s@outlook.com',
      phone: '+234 809 333 4444',
      avatarUrl: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&h=160&fit=crop&crop=faces',
      role: 'vendor',
      status: 'active',
      tier: 'tier1_bronze',
      sellerTrustScore: 75,
      walletBalance: 6500,
      carbonOffsetKg: 12.0,
      tradesCompleted: 2,
      activeListingsCount: 1,
      ordersCount: 1,
      totalSpent: 15000,
      totalSalesVolume: 85000,
      kycStatus: 'pending',
      reportCount: 0,
      createdAt: '2026-02-18'
    },
    {
      uid: 'usr_005_emeka',
      name: 'Emeka Johnson',
      email: 'emeka.j@proton.me',
      phone: '+234 701 888 9999',
      avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=160&h=160&fit=crop&crop=faces',
      role: 'vendor',
      status: 'temporarily_banned',
      tier: 'tier1_bronze',
      sellerTrustScore: 42,
      walletBalance: 0,
      carbonOffsetKg: 5.0,
      tradesCompleted: 1,
      activeListingsCount: 1,
      ordersCount: 0,
      totalSpent: 0,
      totalSalesVolume: 45000,
      kycStatus: 'rejected',
      reportCount: 2,
      banInfo: {
        type: 'temporary',
        reasonCategory: 'Counterfeit / Replica Goods Listing',
        staffNotes: 'Vendor posted replica luxury watches under authentic condition without disclosure. Temporarily suspended for 14 days.',
        bannedAt: '2026-09-10',
        bannedBy: 'Super Admin',
        expiresAt: '2026-09-24'
      },
      appealInfo: {
        appealId: 'apl_9021',
        submittedAt: 'Yesterday at 02:40 PM',
        reason: 'Apology & Removal of Prohibited Inventory',
        userStatement: 'I sincerely apologize to the ReUseIt platform. I mistakenly listed the replica item thinking it was allowed if indicated in the description. I have deleted all replica stock and pledge to only list genuine pre-owned goods.',
        status: 'pending'
      },
      reports: [
        {
          id: 'rep_101',
          reporterId: 'usr_003_chioma',
          reporterName: 'Chioma Eze',
          reportedRole: 'vendor',
          reason: 'Counterfeit / Fake Goods',
          description: 'This vendor is selling counterfeit luxury watches as authentic items. The images clearly show replica engravings.',
          timestamp: '3 days ago',
          relatedListingId: 'lst_suspicious_099',
          status: 'reviewed'
        },
        {
          id: 'rep_102',
          reporterId: 'usr_001_alex',
          reporterName: 'Alex Okafor',
          reportedRole: 'vendor',
          reason: 'Misleading Item Condition',
          description: 'Advertised as brand new authentic, but chat messages indicated it is a Chinese clone.',
          timestamp: '2 days ago',
          relatedListingId: 'lst_suspicious_099',
          status: 'reviewed'
        }
      ],
      createdAt: '2026-02-28'
    },
    {
      uid: 'usr_008_stanley',
      name: 'Stanley Uche',
      email: 'stanley.uche@gmail.com',
      phone: '+234 805 444 8888',
      avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=160&h=160&fit=crop&crop=faces',
      role: 'buyer',
      status: 'active',
      tier: 'tier1_bronze',
      sellerTrustScore: 68,
      walletBalance: 5000,
      carbonOffsetKg: 8.0,
      tradesCompleted: 2,
      activeListingsCount: 0,
      ordersCount: 5,
      totalSpent: 120000,
      totalSalesVolume: 0,
      kycStatus: 'verified',
      reportCount: 1,
      reports: [
        {
          id: 'rep_103',
          reporterId: 'usr_002_tunde',
          reporterName: 'Tunde Bakare',
          reportedRole: 'buyer',
          reason: 'Off-Platform Settlement Solicitation',
          description: 'Buyer repeatedly sent private WhatsApp contact asking to bypass ReUseIt escrow and pay directly via bank transfer.',
          timestamp: 'Yesterday at 11:15 AM',
          status: 'pending'
        }
      ],
      createdAt: '2026-01-20'
    },
    {
      uid: 'usr_009_chuka',
      name: 'Chuka Obi (Deactivated)',
      email: 'chuka.obi@protonmail.com',
      phone: '+234 808 111 2222',
      role: 'vendor',
      status: 'permanently_banned',
      tier: 'tier1_bronze',
      sellerTrustScore: 10,
      walletBalance: 0,
      carbonOffsetKg: 0,
      tradesCompleted: 0,
      activeListingsCount: 0,
      ordersCount: 0,
      totalSpent: 0,
      totalSalesVolume: 0,
      kycStatus: 'rejected',
      reportCount: 3,
      banInfo: {
        type: 'permanent',
        reasonCategory: 'Escrow Payment Fraud & Stolen Identity',
        staffNotes: 'Submitted forged banking payment proof slips to bypass inspection window. Blacklisted permanently across IP & BVN hashes.',
        bannedAt: '2026-08-15',
        bannedBy: 'Super Admin'
      },
      createdAt: '2026-08-01'
    }
  ]);

  // =========================================================================
  // 3. CATEGORIES DATA
  // =========================================================================
  const [categories, setCategories] = useState<MarketplaceCategory[]>([
    {
      id: 'cat_electronics',
      name: 'Electronics & Gadgets',
      slug: 'electronics',
      icon: 'Laptop',
      imageUrl: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=240&h=240&fit=crop',
      itemCount: 3,
      isFeaturedOnHome: true,
      isActive: true,
      description: 'Smartphones, laptops, tablets, smartwatches, and audio gear.',
      createdAt: '2026-01-01'
    },
    {
      id: 'cat_furniture',
      name: 'Home & Furniture',
      slug: 'furniture',
      icon: 'Armchair',
      imageUrl: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=240&h=240&fit=crop',
      itemCount: 1,
      isFeaturedOnHome: true,
      isActive: true,
      description: 'Living room, workspace, chairs, tables, and vintage teak decor.',
      createdAt: '2026-01-01'
    },
    {
      id: 'cat_fashion',
      name: 'Fashion & Apparel',
      slug: 'fashion',
      icon: 'Shirt',
      imageUrl: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=240&h=240&fit=crop',
      itemCount: 1,
      isFeaturedOnHome: true,
      isActive: true,
      description: 'Pre-loved clothing, luxury bags, shoes, and authentic accessories.',
      createdAt: '2026-01-05'
    },
    {
      id: 'cat_vehicles',
      name: 'Vehicles & Bicycles',
      slug: 'vehicles',
      icon: 'Bike',
      imageUrl: 'https://images.unsplash.com/photo-1485965120184-e220f721d03e?w=240&h=240&fit=crop',
      itemCount: 1,
      isFeaturedOnHome: true,
      isActive: true,
      description: 'Commuter bicycles, electric scooters, car spare parts, and accessories.',
      createdAt: '2026-03-01'
    }
  ]);

  // =========================================================================
  // 4. MODALS & INTERACTION STATES
  // =========================================================================
  const [selectedPreviewListing, setSelectedPreviewListing] = useState<CatalogListing | null>(null);
  
  // Flag Modal State
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [listingToFlag, setListingToFlag] = useState<CatalogListing | null>(null);
  const [flagReasonCategory, setFlagReasonCategory] = useState('Counterfeit / Replica Goods');
  const [flagReasonDetails, setFlagReasonDetails] = useState('');

  // Ban User Modal State
  const [isBanModalOpen, setIsBanModalOpen] = useState(false);
  const [userToBan, setUserToBan] = useState<UserRecord | null>(null);
  const [banType, setBanType] = useState<'temporary' | 'permanent'>('temporary');
  const [banDurationDays, setBanDurationDays] = useState(7);
  const [banReasonCategory, setBanReasonCategory] = useState('Counterfeit Goods Listing');
  const [banStaffNotes, setBanStaffNotes] = useState('');
  const [banAutoDelistListings, setBanAutoDelistListings] = useState(true);

  // User Reports Dossier Modal
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [userForReports, setUserForReports] = useState<UserRecord | null>(null);

  // Appeal Review Modal
  const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
  const [userForAppeal, setUserForAppeal] = useState<UserRecord | null>(null);
  const [appealReviewNotes, setAppealReviewNotes] = useState('');

  // Category Modals
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MarketplaceCategory | null>(null);
  const [categoryFormName, setCategoryFormName] = useState('');
  const [categoryFormSlug, setCategoryFormSlug] = useState('');
  const [categoryFormImageUrl, setCategoryFormImageUrl] = useState('');
  const [categoryFormDescription, setCategoryFormDescription] = useState('');
  const [categoryFormFeatured, setCategoryFormFeatured] = useState(true);

  // =========================================================================
  // 5. LISTING MODERATION ACTIONS
  // =========================================================================
  const handleToggleFeatured = (id: string) => {
    setListings(prev => prev.map(l => {
      if (l.id === id) {
        const next = !l.isFeatured;
        showToast(next ? `"${l.title}" marked as Featured!` : `"${l.title}" unfeatured.`);
        return { ...l, isFeatured: next };
      }
      return l;
    }));
  };

  // Enlist a delisted item back to active
  const handleEnlistListing = (id: string) => {
    AdminApiClient.moderateListing(id, 'ACTIVE').catch(console.error);
    setListings(prev => prev.map(l => {
      if (l.id === id) {
        showToast(`Listing "${l.title}" has been re-enlisted & published!`);
        return { 
          ...l, 
          status: 'active', 
          delistedReason: undefined, 
          delistedAt: undefined 
        };
      }
      return l;
    }));
    if (selectedPreviewListing?.id === id) {
      setSelectedPreviewListing(prev => prev ? { ...prev, status: 'active', delistedReason: undefined } : null);
    }
  };

  // Delist an item
  const handleDelistListing = (id: string, reason?: string) => {
    const r = reason || 'Manually delisted by Super Admin';
    AdminApiClient.moderateListing(id, 'DELISTED', r).catch(console.error);
    setListings(prev => prev.map(l => {
      if (l.id === id) {
        showToast(`Listing "${l.title}" delisted.`);
        return { 
          ...l, 
          status: 'delisted', 
          delistedReason: r, 
          delistedAt: 'Just now' 
        };
      }
      return l;
    }));
    if (selectedPreviewListing?.id === id) {
      setSelectedPreviewListing(prev => prev ? { ...prev, status: 'delisted', delistedReason: r } : null);
    }
  };

  // Open flag dialog
  const handleOpenFlagModal = (listing: CatalogListing) => {
    setListingToFlag(listing);
    setFlagReasonCategory('Counterfeit / Replica Goods');
    setFlagReasonDetails('');
    setIsFlagModalOpen(true);
  };

  // Submit manual flag
  const handleSubmitFlag = () => {
    if (!listingToFlag) return;
    const combinedReason = flagReasonDetails.trim() 
      ? `${flagReasonCategory}: ${flagReasonDetails.trim()}`
      : flagReasonCategory;

    AdminApiClient.moderateListing(listingToFlag.id, 'FLAGGED', combinedReason).catch(console.error);
    setListings(prev => prev.map(l => {
      if (l.id === listingToFlag.id) {
        return { 
          ...l, 
          status: 'flagged', 
          flagReason: combinedReason,
          aiModerationStatus: 'flagged'
        };
      }
      return l;
    }));

    if (selectedPreviewListing?.id === listingToFlag.id) {
      setSelectedPreviewListing(prev => prev ? { 
        ...prev, 
        status: 'flagged', 
        flagReason: combinedReason 
      } : null);
    }

    setIsFlagModalOpen(false);
    setListingToFlag(null);
    showToast(`Listing flagged: "${listingToFlag.title}" moved to Review Queue.`);
  };

  // Unflag an item
  const handleClearFlag = (id: string) => {
    setListings(prev => prev.map(l => {
      if (l.id === id) {
        showToast(`Flag cleared for "${l.title}". Listing restored to Active!`);
        return { ...l, status: 'active', flagReason: undefined, aiModerationStatus: 'clean' };
      }
      return l;
    }));
    if (selectedPreviewListing?.id === id) {
      setSelectedPreviewListing(prev => prev ? { ...prev, status: 'active', flagReason: undefined } : null);
    }
  };

  // Delete listing permanently
  const handleDeleteListing = (id: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this listing? This action is immutable.')) return;
    setListings(prev => prev.filter(l => l.id !== id));
    if (selectedPreviewListing?.id === id) {
      setSelectedPreviewListing(null);
    }
    showToast('Listing permanently purged from database.');
  };

  // =========================================================================
  // 6. USER BANNING & APPEAL ACTIONS
  // =========================================================================
  const handleOpenBanModal = (user: UserRecord) => {
    setUserToBan(user);
    setBanType('temporary');
    setBanDurationDays(7);
    setBanReasonCategory('Counterfeit Goods Listing');
    setBanStaffNotes('');
    setBanAutoDelistListings(true);
    setIsBanModalOpen(true);
  };

  const handleConfirmBan = () => {
    if (!userToBan) return;

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + banDurationDays);
    const expiresStr = banType === 'temporary' ? expiryDate.toISOString().split('T')[0] : undefined;

    const banData: UserBanInfo = {
      type: banType,
      reasonCategory: banReasonCategory,
      staffNotes: banStaffNotes.trim() || 'Direct action by Super Admin.',
      bannedAt: new Date().toISOString().split('T')[0],
      bannedBy: 'Super Admin',
      expiresAt: expiresStr
    };

    const nextStatus = banType === 'temporary' ? 'temporarily_banned' : 'permanently_banned';

    // Update user record
    setParticipants(prev => prev.map(u => {
      if (u.uid === userToBan.uid) {
        return {
          ...u,
          status: nextStatus,
          banInfo: banData
        };
      }
      return u;
    }));

    // Optionally auto-delist all active listings by this user
    if (banAutoDelistListings) {
      setListings(prev => prev.map(l => {
        if (l.sellerId === userToBan.uid && l.status === 'active') {
          return {
            ...l,
            status: 'delisted',
            delistedReason: `Vendor account suspended: ${banReasonCategory}`,
            delistedAt: 'Just now'
          };
        }
        return l;
      }));
    }

    setIsBanModalOpen(false);
    setUserToBan(null);
    showToast(`User ${userToBan.name} has been ${banType === 'temporary' ? 'temporarily suspended' : 'permanently banned'}!`);
  };

  // Unban user after submitted appeal or directly
  const handleApproveAppealAndUnban = (user: UserRecord, reenlistListings: boolean = true) => {
    setParticipants(prev => prev.map(u => {
      if (u.uid === user.uid) {
        return {
          ...u,
          status: 'active',
          banInfo: undefined,
          appealInfo: u.appealInfo ? {
            ...u.appealInfo,
            status: 'approved',
            reviewedAt: 'Just now',
            reviewedBy: 'Super Admin',
            resolutionNotes: appealReviewNotes.trim() || 'Appeal approved after verified compliance.'
          } : undefined
        };
      }
      return u;
    }));

    // Optionally re-enlist their delisted items
    if (reenlistListings) {
      setListings(prev => prev.map(l => {
        if (l.sellerId === user.uid && l.status === 'delisted' && l.delistedReason?.includes('suspended')) {
          return {
            ...l,
            status: 'active',
            delistedReason: undefined
          };
        }
        return l;
      }));
    }

    setIsAppealModalOpen(false);
    setUserForAppeal(null);
    setAppealReviewNotes('');
    showToast(`Appeal approved! ${user.name} has been unbanned and restored.`);
  };

  const handleRejectAppeal = (user: UserRecord) => {
    setParticipants(prev => prev.map(u => {
      if (u.uid === user.uid && u.appealInfo) {
        return {
          ...u,
          appealInfo: {
            ...u.appealInfo,
            status: 'rejected',
            reviewedAt: 'Just now',
            reviewedBy: 'Super Admin',
            resolutionNotes: appealReviewNotes.trim() || 'Appeal rejected. Ban conditions remain active.'
          }
        };
      }
      return u;
    }));

    setIsAppealModalOpen(false);
    setUserForAppeal(null);
    setAppealReviewNotes('');
    showToast(`Appeal rejected for ${user.name}. Ban upheld.`);
  };

  // Direct unban without appeal
  const handleDirectUnban = (user: UserRecord) => {
    if (!window.confirm(`Are you sure you want to lift the ban and restore ${user.name}'s account?`)) return;
    setParticipants(prev => prev.map(u => {
      if (u.uid === user.uid) {
        return {
          ...u,
          status: 'active',
          banInfo: undefined
        };
      }
      return u;
    }));
    showToast(`${user.name} has been unbanned successfully!`);
  };

  // =========================================================================
  // 7. CSV / EXCEL EXPORT FUNCTIONS
  // =========================================================================
  const exportParticipantsToCSV = () => {
    const headers = [
      'UID',
      'Name',
      'Email',
      'Phone',
      'Role',
      'Account Status',
      'KYC Tier',
      'Trust Score',
      'Trades Completed',
      'Wallet Balance (NGN)',
      'Carbon Offset (kg)',
      'Report Count',
      'Ban Type',
      'Ban Reason',
      'Ban Expiry',
      'Appeal Status',
      'Joined Date'
    ];

    const rows = participants.map(u => [
      `"${u.uid}"`,
      `"${u.name.replace(/"/g, '""')}"`,
      `"${u.email}"`,
      `"${u.phone || ''}"`,
      `"${u.role}"`,
      `"${u.status}"`,
      `"${u.tier}"`,
      u.sellerTrustScore,
      u.tradesCompleted,
      u.walletBalance,
      u.carbonOffsetKg,
      u.reportCount || 0,
      `"${u.banInfo?.type || 'None'}"`,
      `"${(u.banInfo?.reasonCategory || '').replace(/"/g, '""')}"`,
      `"${u.banInfo?.expiresAt || 'N/A'}"`,
      `"${u.appealInfo?.status || 'None'}"`,
      `"${u.createdAt || ''}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ReUseIt_Marketplace_Participants_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Marketplace participants exported to CSV!');
  };

  const exportListingsToCSV = () => {
    const headers = [
      'Listing ID',
      'Title',
      'Category',
      'Price (NGN)',
      'Condition',
      'Eco Score',
      'CO2 Saved (kg)',
      'Seller Name',
      'Seller UID',
      'Status',
      'Flag Reason',
      'Delisted Reason',
      'AI Moderation',
      'Location',
      'Created At'
    ];

    const rows = listings.map(l => [
      `"${l.id}"`,
      `"${l.title.replace(/"/g, '""')}"`,
      `"${l.category}"`,
      l.price,
      `"${l.condition}"`,
      l.ecoScore,
      l.co2SavingsKg,
      `"${l.sellerName.replace(/"/g, '""')}"`,
      `"${l.sellerId}"`,
      `"${l.status}"`,
      `"${(l.flagReason || '').replace(/"/g, '""')}"`,
      `"${(l.delistedReason || '').replace(/"/g, '""')}"`,
      `"${l.aiModerationStatus || 'clean'}"`,
      `"${l.location}"`,
      `"${l.createdAt}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `ReUseIt_Catalog_Listings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Catalog listings exported to CSV!');
  };

  // =========================================================================
  // 8. CATEGORY MANAGEMENT ACTIONS
  // =========================================================================
  const handleOpenAddCategory = () => {
    setCategoryFormName('');
    setCategoryFormSlug('');
    setCategoryFormImageUrl('https://images.unsplash.com/photo-1509391365360-2e959784a276?w=240&h=240&fit=crop');
    setCategoryFormDescription('');
    setCategoryFormFeatured(true);
    setIsAddCategoryOpen(true);
  };

  const handleOpenEditCategory = (cat: MarketplaceCategory) => {
    setEditingCategory(cat);
    setCategoryFormName(cat.name);
    setCategoryFormSlug(cat.slug);
    setCategoryFormImageUrl(cat.imageUrl || '');
    setCategoryFormDescription(cat.description || '');
    setCategoryFormFeatured(cat.isFeaturedOnHome);
    setIsEditCategoryOpen(true);
  };

  const handleSaveNewCategory = () => {
    if (!categoryFormName.trim()) return;
    const slug = categoryFormSlug.trim() || categoryFormName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const newCat: MarketplaceCategory = {
      id: `cat_${Date.now()}`,
      name: categoryFormName.trim(),
      slug,
      icon: 'Tag',
      imageUrl: categoryFormImageUrl.trim() || 'https://images.unsplash.com/photo-1509391365360-2e959784a276?w=240&h=240&fit=crop',
      itemCount: 0,
      isFeaturedOnHome: categoryFormFeatured,
      isActive: true,
      description: categoryFormDescription.trim(),
      createdAt: 'Just now'
    };
    setCategories(prev => [...prev, newCat]);
    setIsAddCategoryOpen(false);
    showToast(`New category "${newCat.name}" added to catalog!`);
  };

  const handleUpdateCategory = () => {
    if (!editingCategory || !categoryFormName.trim()) return;
    setCategories(prev => prev.map(c => {
      if (c.id === editingCategory.id) {
        return {
          ...c,
          name: categoryFormName.trim(),
          slug: categoryFormSlug.trim() || c.slug,
          imageUrl: categoryFormImageUrl.trim() || c.imageUrl,
          description: categoryFormDescription.trim(),
          isFeaturedOnHome: categoryFormFeatured
        };
      }
      return c;
    }));
    setIsEditCategoryOpen(false);
    setEditingCategory(null);
    showToast('Category updated successfully.');
  };

  const handleToggleCategoryFeatured = (id: string) => {
    setCategories(prev => prev.map(c => {
      if (c.id === id) {
        return { ...c, isFeaturedOnHome: !c.isFeaturedOnHome };
      }
      return c;
    }));
  };

  const handleDeleteCategory = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    setCategories(prev => prev.filter(c => c.id !== id));
    showToast('Category deleted.');
  };

  const getCategoryItemCount = (categoryName: string, categorySlug: string) => {
    return listings.filter(l => 
      l.category.toLowerCase() === categoryName.toLowerCase() ||
      categoryName.toLowerCase().includes(l.category.toLowerCase()) ||
      l.category.toLowerCase() === categorySlug.toLowerCase()
    ).length;
  };

  // =========================================================================
  // 9. FILTERING
  // =========================================================================
  const filteredListings = listings.filter(l => {
    let matchTab = true;
    if (tab === 'all') matchTab = true;
    else if (tab === 'flagged') matchTab = l.status === 'flagged';
    else if (tab === 'delisted') matchTab = l.status === 'delisted';

    const matchSearch = l.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        l.sellerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        l.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = categoryFilter === 'all' || l.category.toLowerCase() === categoryFilter.toLowerCase();
    return matchTab && matchSearch && matchCat;
  });

  const filteredParticipants = participants.filter(u => {
    // Role filter
    let matchRole = true;
    if (participantRoleFilter === 'vendors') {
      matchRole = u.role === 'vendor' || u.role === 'both';
    } else if (participantRoleFilter === 'buyers') {
      matchRole = u.role === 'buyer' || u.role === 'both';
    } else if (participantRoleFilter === 'reported') {
      matchRole = (u.reportCount || 0) > 0;
    } else if (participantRoleFilter === 'banned') {
      matchRole = u.status === 'temporarily_banned' || u.status === 'permanently_banned' || u.status === 'suspended';
    } else if (participantRoleFilter === 'appeals') {
      matchRole = u.appealInfo?.status === 'pending';
    }

    // Search
    const q = participantSearch.toLowerCase();
    const matchSearch = u.name.toLowerCase().includes(q) ||
                        u.email.toLowerCase().includes(q) ||
                        (u.phone && u.phone.includes(q)) ||
                        u.uid.toLowerCase().includes(q);

    return matchRole && matchSearch;
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Toast Banner */}
      {actionToast && (
        <div className="fixed top-24 right-8 z-50 p-4 rounded-2xl bg-brand-500 text-forest-void font-extrabold text-xs shadow-glow-md flex items-center gap-2 animate-bounce-short">
          <CheckCircle2 className="w-5 h-5" />
          <span>{actionToast}</span>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] pb-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => setTab('all')}
            className={`px-4 py-2.5 rounded-full text-xs font-bold transition-all
              ${tab === 'all'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            All Catalog Items ({listings.length})
          </button>

          <button
            onClick={() => setTab('flagged')}
            className={`relative px-4 py-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5
              ${tab === 'flagged'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            <Flag className="w-3.5 h-3.5" />
            <span>Flagged for Review</span>
            {listings.filter(l => l.status === 'flagged').length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-rose-500 text-white font-extrabold">
                {listings.filter(l => l.status === 'flagged').length}
              </span>
            )}
          </button>

          <button
            onClick={() => setTab('delisted')}
            className={`relative px-4 py-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5
              ${tab === 'delisted'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            <EyeOff className="w-3.5 h-3.5" />
            <span>Delisted Items</span>
            {listings.filter(l => l.status === 'delisted').length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500 text-forest-void font-extrabold">
                {listings.filter(l => l.status === 'delisted').length}
              </span>
            )}
          </button>

          <button
            onClick={() => setTab('categories')}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all
              ${tab === 'categories'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Marketplace Categories ({categories.length})</span>
          </button>

          <button
            onClick={() => setTab('participants')}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all relative
              ${tab === 'participants'
                ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                : 'text-[#90cbb7] hover:text-white dark:text-[#90cbb7] dark:hover:text-white light:text-[#527063] light:hover:text-[#0D1C17]'
              }
            `}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Vendors & Buyers ({participants.length})</span>
            {participants.filter(p => (p.reportCount || 0) > 0).length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-rose-500 text-white font-extrabold animate-pulse">
                {participants.filter(p => (p.reportCount || 0) > 0).length} reported
              </span>
            )}
          </button>
        </div>

        {/* Global Export & Header Actions */}
        <div className="flex items-center gap-2">
          {tab === 'categories' ? (
            <button
              onClick={handleOpenAddCategory}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add New Category</span>
            </button>
          ) : tab === 'participants' ? (
            <button
              onClick={exportParticipantsToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border border-brand-500/40 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 shadow-glow-sm transition-all"
              title="Export all participant vendors and buyers with ban & report records to CSV"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Export Participants (.csv)</span>
            </button>
          ) : (
            <button
              onClick={exportListingsToCSV}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border border-brand-500/40 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 shadow-glow-sm transition-all"
              title="Export filtered catalog listings to CSV"
            >
              <Download className="w-4 h-4" />
              <span>Export Listings (.csv)</span>
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB A: CATALOG LISTINGS (ALL / FLAGGED / DELISTED) */}
      {/* ========================================================================= */}
      {(tab === 'all' || tab === 'flagged' || tab === 'delisted') && (
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl border
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC]"
          >
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items by title, ID, or seller..."
                className="w-full pl-10 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Filter className="w-4 h-4 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3.5 py-2 rounded-xl text-xs font-semibold outline-none border cursor-pointer
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Listings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((item) => (
              <div
                key={item.id}
                className={`rounded-2xl border overflow-hidden transition-all duration-300 flex flex-col justify-between
                  ${item.status === 'flagged' 
                    ? 'border-rose-500/50 bg-gradient-to-b from-[#1c1214] to-[#152a23]' 
                    : item.status === 'delisted'
                    ? 'border-amber-500/40 bg-[#14231e] opacity-90'
                    : 'border-[#22493c] bg-[#152a23] hover:border-brand-500/50'
                  }
                  light:bg-white light:border-[#D5E4DC] shadow-card`
                }
              >
                <div>
                  {/* Image Container with Preview Trigger */}
                  <div 
                    onClick={() => setSelectedPreviewListing(item)}
                    className="relative h-48 w-full bg-[#10231d] overflow-hidden cursor-pointer group"
                  >
                    <img 
                      src={item.imageUrls[0]} 
                      alt={item.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="px-3 py-1.5 rounded-full bg-forest-void/90 text-brand-400 font-bold text-xs flex items-center gap-1.5 shadow-glow-sm">
                        <Eye className="w-3.5 h-3.5" /> Preview Item
                      </span>
                    </div>

                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#10231d]/90 text-brand-400 border border-brand-500/30 backdrop-blur-md">
                        {item.category}
                      </span>
                      {item.isFeatured && (
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/90 text-forest-void backdrop-blur-md flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Featured
                        </span>
                      )}
                    </div>

                    <div className="absolute top-3 right-3">
                      <StatusBadge status={item.status} />
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-5 space-y-3 text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <h4 
                        onClick={() => setSelectedPreviewListing(item)}
                        className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17] line-clamp-1 hover:text-brand-400 cursor-pointer transition-colors"
                      >
                        {item.title}
                      </h4>
                      <span className="font-extrabold text-sm text-emerald-400 shrink-0">
                        ₦{item.price.toLocaleString()}
                      </span>
                    </div>

                    <p className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] line-clamp-2">
                      {item.description}
                    </p>

                    <div className="flex items-center gap-4 text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      <span>Condition: <strong className="text-white dark:text-white light:text-[#0D1C17]">{item.condition}</strong></span>
                      <span className="flex items-center gap-1 text-emerald-400">
                        <Leaf className="w-3.5 h-3.5" /> {item.co2SavingsKg}kg CO₂ saved
                      </span>
                    </div>

                    {/* Flag Reason Callout */}
                    {item.flagReason && (
                      <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-[11px] flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                        <div>
                          <strong className="block text-rose-200">Flagged For Moderation:</strong>
                          <span>{item.flagReason}</span>
                        </div>
                      </div>
                    )}

                    {/* Delisted Callout */}
                    {item.delistedReason && item.status === 'delisted' && (
                      <div className="p-3 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] flex items-start gap-2">
                        <EyeOff className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                          <strong className="block text-amber-200">Delisted from Feed:</strong>
                          <span>{item.delistedReason}</span>
                        </div>
                      </div>
                    )}

                    <div className="pt-2 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex justify-between text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      <span>Seller: <strong className="text-white dark:text-white light:text-[#0D1C17]">{item.sellerName}</strong></span>
                      <span>{item.location}</span>
                    </div>
                  </div>
                </div>

                {/* Moderation Actions Footer */}
                <div className="p-4 bg-[#10231d]/60 dark:bg-[#10231d]/60 light:bg-gray-50 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE] flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedPreviewListing(item)}
                      className="p-2 rounded-xl text-xs font-semibold text-brand-400 hover:bg-brand-500/15 transition-colors flex items-center gap-1"
                      title="Preview full item details"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Preview</span>
                    </button>

                    <button
                      onClick={() => handleToggleFeatured(item.id)}
                      className={`p-2 rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors ${item.isFeatured ? 'text-amber-400 hover:bg-amber-500/15' : 'text-[#90cbb7] hover:text-white hover:bg-[#22493c]'}`}
                      title={item.isFeatured ? 'Unfeature item' : 'Feature on app carousel'}
                    >
                      <Sparkles className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* If Item is Delisted: Show Enlist Button */}
                    {item.status === 'delisted' ? (
                      <button
                        onClick={() => handleEnlistListing(item.id)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-bold text-forest-void bg-emerald-400 hover:bg-emerald-300 shadow-glow-sm transition-all flex items-center gap-1.5"
                        title="Re-activate and publish to mobile marketplace"
                      >
                        <ArrowUpCircle className="w-3.5 h-3.5 stroke-[2.5]" />
                        <span>Enlist Item</span>
                      </button>
                    ) : item.status === 'flagged' ? (
                      /* If Item is Flagged: Show Unflag & Delist Buttons */
                      <>
                        <button
                          onClick={() => handleClearFlag(item.id)}
                          className="px-3 py-1.5 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 transition-all flex items-center gap-1"
                          title="Clear flags and restore to active"
                        >
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                          <span>Unflag</span>
                        </button>
                        <button
                          onClick={() => handleDelistListing(item.id, item.flagReason)}
                          className="p-2 rounded-xl text-amber-400 hover:bg-amber-500/15 transition-colors"
                          title="Delist from Feed"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      /* Active item: Show Flag & Delist Buttons */
                      <>
                        <button
                          onClick={() => handleOpenFlagModal(item)}
                          className="p-2 rounded-xl text-rose-400 hover:bg-rose-500/15 transition-colors"
                          title="Manually Flag Item"
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelistListing(item.id)}
                          className="p-2 rounded-xl text-amber-400 hover:bg-amber-500/15 transition-colors"
                          title="Delist / Hide from Feed"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => handleDeleteListing(item.id)}
                      className="p-2 rounded-xl text-rose-400 hover:bg-rose-500/15 transition-colors"
                      title="Delete Permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredListings.length === 0 && (
            <div className="p-12 text-center rounded-2xl border border-dashed border-[#22493c] text-[#90cbb7]">
              <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="font-bold">No catalog listings found.</p>
              <p className="text-xs text-[#90cbb7]/70 mt-1">Try refining your search terms or filter criteria.</p>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB B: VENDORS & BUYERS DIRECTORY (PARTICIPANTS) */}
      {/* ========================================================================= */}
      {tab === 'participants' && (
        <div className="space-y-6">
          {/* Header Metric Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC]">
              <span className="text-[11px] font-semibold text-[#90cbb7] block">Total Vendors (Sellers)</span>
              <span className="text-xl font-extrabold text-white light:text-[#0D1C17]">
                {participants.filter(p => p.role === 'vendor' || p.role === 'both').length}
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC]">
              <span className="text-[11px] font-semibold text-[#90cbb7] block">Total Buyers</span>
              <span className="text-xl font-extrabold text-white light:text-[#0D1C17]">
                {participants.filter(p => p.role === 'buyer' || p.role === 'both').length}
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-rose-500/30 bg-rose-500/10 light:bg-rose-50 light:border-rose-200">
              <span className="text-[11px] font-semibold text-rose-300 light:text-rose-700 block">Reported Users</span>
              <span className="text-xl font-extrabold text-rose-400 light:text-rose-700">
                {participants.filter(p => (p.reportCount || 0) > 0).length} Pending Review
              </span>
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-amber-500/30 bg-amber-500/10 light:bg-amber-50 light:border-amber-200">
              <span className="text-[11px] font-semibold text-amber-300 light:text-amber-700 block">Active Appeals</span>
              <span className="text-xl font-extrabold text-amber-400 light:text-amber-700">
                {participants.filter(p => p.appealInfo?.status === 'pending').length} Awaiting Unban
              </span>
            </div>
          </div>

          {/* Sub-Filters & Search Toolbar */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 rounded-2xl border
            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
            light:bg-white light:border-[#D5E4DC]"
          >
            {/* Filter Pills */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setParticipantRoleFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all
                  ${participantRoleFilter === 'all'
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white bg-[#10231d] border border-[#22493c]'
                  }
                `}
              >
                All ({participants.length})
              </button>

              <button
                type="button"
                onClick={() => setParticipantRoleFilter('vendors')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all
                  ${participantRoleFilter === 'vendors'
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white bg-[#10231d] border border-[#22493c]'
                  }
                `}
              >
                Vendors Only ({participants.filter(p => p.role === 'vendor' || p.role === 'both').length})
              </button>

              <button
                type="button"
                onClick={() => setParticipantRoleFilter('buyers')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all
                  ${participantRoleFilter === 'buyers'
                    ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white bg-[#10231d] border border-[#22493c]'
                  }
                `}
              >
                Buyers Only ({participants.filter(p => p.role === 'buyer' || p.role === 'both').length})
              </button>

              <button
                type="button"
                onClick={() => setParticipantRoleFilter('reported')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1
                  ${participantRoleFilter === 'reported'
                    ? 'bg-rose-500 text-white shadow-glow-sm'
                    : 'text-rose-300 hover:text-white bg-rose-500/15 border border-rose-500/30'
                  }
                `}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Reported ({participants.filter(p => (p.reportCount || 0) > 0).length})</span>
              </button>

              <button
                type="button"
                onClick={() => setParticipantRoleFilter('banned')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all
                  ${participantRoleFilter === 'banned'
                    ? 'bg-amber-500 text-forest-void shadow-glow-sm'
                    : 'text-[#90cbb7] hover:text-white bg-[#10231d] border border-[#22493c]'
                  }
                `}
              >
                Banned / Suspended ({participants.filter(p => p.status === 'temporarily_banned' || p.status === 'permanently_banned').length})
              </button>

              <button
                type="button"
                onClick={() => setParticipantRoleFilter('appeals')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1
                  ${participantRoleFilter === 'appeals'
                    ? 'bg-purple-500 text-white shadow-glow-sm'
                    : 'text-purple-300 hover:text-white bg-purple-500/15 border border-purple-500/30'
                  }
                `}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Appeals Pending ({participants.filter(p => p.appealInfo?.status === 'pending').length})</span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7]" />
              <input
                type="text"
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                placeholder="Search by name, email, phone, UID..."
                className="w-full pl-10 pr-4 py-2 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] border-[#22493c] text-white focus:border-brand-500"
              />
            </div>
          </div>

          {/* Participants Table */}
          <div className="rounded-2xl border overflow-hidden bg-[#152a23] dark:bg-[#152a23] border-[#22493c] light:bg-white light:border-[#D5E4DC]">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-[#22493c] bg-[#10231d] text-[#90cbb7] uppercase tracking-wider text-[10px] font-bold">
                  <tr>
                    <th className="py-3.5 px-4">User Identity</th>
                    <th className="py-3.5 px-4">Role / Type</th>
                    <th className="py-3.5 px-4">Market Activity</th>
                    <th className="py-3.5 px-4">Account Status</th>
                    <th className="py-3.5 px-4">Reports & Flags</th>
                    <th className="py-3.5 px-4 text-right">Moderation Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c] text-white">
                  {filteredParticipants.map((user) => (
                    <tr key={user.uid} className="hover:bg-[#10231d]/60 transition-colors">
                      {/* Identity */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={user.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=160&h=160&fit=crop&crop=faces'} 
                            alt={user.name} 
                            className="w-9 h-9 rounded-full object-cover border border-brand-500/40 shrink-0"
                          />
                          <div>
                            <p className="font-bold text-xs">{user.name}</p>
                            <p className="text-[11px] text-[#90cbb7]">{user.email}</p>
                            <p className="text-[10px] text-[#90cbb7]/60 font-mono">{user.uid}</p>
                          </div>
                        </div>
                      </td>

                      {/* Role & KYC */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            user.role === 'vendor' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                            user.role === 'buyer' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                            'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          }`}>
                            {user.role === 'both' ? 'Buyer & Vendor' : user.role}
                          </span>
                          <div className="text-[10px] text-[#90cbb7]">
                            KYC: <strong className="text-white capitalize">{user.kycStatus}</strong>
                          </div>
                        </div>
                      </td>

                      {/* Activity */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5 text-[11px]">
                          {user.role !== 'buyer' && (
                            <p>Listings: <strong className="text-brand-400">{user.activeListingsCount || 0} active</strong> ({user.tradesCompleted} sold)</p>
                          )}
                          <p>Volume: <strong className="text-emerald-400">₦{(user.totalSalesVolume || user.totalSpent || 0).toLocaleString()}</strong></p>
                          <p className="text-[10px] text-[#90cbb7]">Trust Score: <strong className="text-white">{user.sellerTrustScore}%</strong></p>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        {user.status === 'active' ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3 text-emerald-400" /> Active
                          </span>
                        ) : user.status === 'temporarily_banned' ? (
                          <div className="space-y-1">
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1 w-fit">
                              <Clock className="w-3 h-3 text-amber-400" /> Temp Suspended
                            </span>
                            {user.banInfo?.expiresAt && (
                              <p className="text-[10px] text-amber-400/80 font-mono">Until: {user.banInfo.expiresAt}</p>
                            )}
                          </div>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1 w-fit">
                            <Ban className="w-3 h-3 text-rose-400" /> Permanent Ban
                          </span>
                        )}
                      </td>

                      {/* Reports & Appeals */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1.5">
                          {(user.reportCount || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                setUserForReports(user);
                                setIsReportsModalOpen(true);
                              }}
                              className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30 flex items-center gap-1 transition-all"
                            >
                              <AlertTriangle className="w-3 h-3 text-rose-400" />
                              <span>{user.reportCount} Reports Filed</span>
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#90cbb7]/60">Clean Record</span>
                          )}

                          {user.appealInfo && user.appealInfo.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => {
                                setUserForAppeal(user);
                                setIsAppealModalOpen(true);
                              }}
                              className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/40 hover:bg-purple-500/30 flex items-center gap-1 animate-pulse"
                            >
                              <RotateCcw className="w-3 h-3 text-purple-400" />
                              <span>Appeal Pending Review</span>
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* If Banned: Show Unban Button / Review Appeal */}
                          {user.status === 'temporarily_banned' || user.status === 'permanently_banned' ? (
                            user.appealInfo && user.appealInfo.status === 'pending' ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setUserForAppeal(user);
                                  setIsAppealModalOpen(true);
                                }}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-purple-500 text-white hover:bg-purple-400 shadow-glow-sm transition-all flex items-center gap-1"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span>Review Appeal</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleDirectUnban(user)}
                                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-500 text-forest-void hover:bg-emerald-400 transition-all flex items-center gap-1"
                              >
                                <CheckCircle className="w-3.5 h-3.5 stroke-[2.5]" />
                                <span>Unban User</span>
                              </button>
                            )
                          ) : (
                            /* If Active: Show Ban / Suspend Button */
                            <button
                              type="button"
                              onClick={() => handleOpenBanModal(user)}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500 hover:text-white transition-all flex items-center gap-1"
                            >
                              <Ban className="w-3.5 h-3.5" />
                              <span>Ban / Suspend</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredParticipants.length === 0 && (
              <div className="p-8 text-center text-[#90cbb7]">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="font-bold">No participants found matching current filter.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB C: MARKETPLACE CATEGORIES MANAGER */}
      {/* ========================================================================= */}
      {tab === 'categories' && (
        <div className="space-y-6">
          <div className="p-5 rounded-2xl bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] dark:border-[#22493c] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-white dark:text-white light:text-[#0D1C17] flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-brand-400" />
                Mobile App Catalog Categories & Taxonomy
              </h3>
              <p className="mt-1 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                Categories configured here are published directly to the Flutter mobile app home carousel and listing filter bar.
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#10231d] border border-[#22493c] text-xs font-semibold text-brand-400">
              <span>{categories.filter(c => c.isFeaturedOnHome).length} Featured on App Home</span>
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => {
              const liveCount = getCategoryItemCount(cat.name, cat.slug);
              return (
                <div
                  key={cat.id}
                  className="rounded-2xl border overflow-hidden transition-all duration-300 bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40 flex flex-col justify-between"
                >
                  <div>
                    {/* Cover Header */}
                    <div className="relative h-28 w-full bg-[#10231d] overflow-hidden">
                      {cat.imageUrl ? (
                        <img 
                          src={cat.imageUrl} 
                          alt={cat.name} 
                          className="w-full h-full object-cover opacity-80 hover:scale-105 transition-transform duration-500" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-brand-500/10 text-brand-400">
                          <Tag className="w-8 h-8" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#152a23] via-transparent to-black/40" />

                      {/* Featured Badge */}
                      <div className="absolute top-3 left-3">
                        {cat.isFeaturedOnHome ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-brand-500 text-forest-void shadow-glow-sm flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Featured on Mobile
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-black/60 text-[#90cbb7] backdrop-blur-md">
                            Standard
                          </span>
                        )}
                      </div>

                      {/* Items Count Badge */}
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#10231d]/90 text-white border border-[#22493c] shadow-sm">
                          {liveCount} {liveCount === 1 ? 'item' : 'items'} listed
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-sm text-white dark:text-white light:text-[#0D1C17]">
                          {cat.name}
                        </h4>
                        <span className="font-mono text-[10px] text-[#90cbb7]/80 bg-[#10231d] px-2 py-0.5 rounded border border-[#22493c]">
                          /{cat.slug}
                        </span>
                      </div>

                      <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] line-clamp-2">
                        {cat.description || 'No description provided.'}
                      </p>
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div className="p-3.5 bg-[#10231d]/60 border-t border-[#22493c] flex items-center justify-between">
                    <button
                      onClick={() => handleToggleCategoryFeatured(cat.id)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5
                        ${cat.isFeaturedOnHome 
                          ? 'bg-brand-500/20 text-brand-300 border border-brand-500/30' 
                          : 'text-[#90cbb7] hover:text-white bg-[#152a23] border border-[#22493c]'
                        }
                      `}
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>{cat.isFeaturedOnHome ? 'Featured' : 'Make Featured'}</span>
                    </button>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleOpenEditCategory(cat)}
                        className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#22493c] transition-colors"
                        title="Edit Category"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15 transition-colors"
                        title="Delete Category"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 10. MODAL: ITEM PREVIEW MODAL */}
      {/* ========================================================================= */}
      {selectedPreviewListing && (
        <Modal
          isOpen={!!selectedPreviewListing}
          onClose={() => setSelectedPreviewListing(null)}
          title="Marketplace Listing Preview & Moderation"
          subtitle={`Inspect catalog item ID: ${selectedPreviewListing.id}`}
          maxWidth="xl"
        >
          <div className="space-y-6 text-xs">
            {/* Top Product Photos & Core Meta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Photo Showcase */}
              <div className="space-y-3">
                <div className="relative h-64 rounded-2xl overflow-hidden border border-[#22493c] bg-[#10231d]">
                  <img 
                    src={selectedPreviewListing.imageUrls[0]} 
                    alt={selectedPreviewListing.title}
                    className="w-full h-full object-cover" 
                  />
                  <div className="absolute top-3 left-3">
                    <StatusBadge status={selectedPreviewListing.status} />
                  </div>
                  {selectedPreviewListing.isFeatured && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500 text-forest-void font-extrabold text-[10px] flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Featured on App Home
                    </div>
                  )}
                </div>

                {selectedPreviewListing.imageUrls.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {selectedPreviewListing.imageUrls.map((img, idx) => (
                      <div key={idx} className="h-16 rounded-xl overflow-hidden border border-[#22493c]">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Core Details */}
              <div className="space-y-4">
                <div>
                  <span className="text-[10px] uppercase font-bold text-brand-400 tracking-wider">
                    {selectedPreviewListing.category} • {selectedPreviewListing.condition}
                  </span>
                  <h3 className="text-lg font-bold text-white mt-1">
                    {selectedPreviewListing.title}
                  </h3>
                  <div className="mt-2 text-2xl font-extrabold text-emerald-400">
                    ₦{selectedPreviewListing.price.toLocaleString()}
                  </div>
                </div>

                {/* Eco impact badge */}
                <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 flex items-center justify-between">
                  <span className="flex items-center gap-2 font-semibold">
                    <Leaf className="w-4 h-4 text-emerald-400" />
                    Circular Carbon Offset:
                  </span>
                  <span className="font-extrabold">{selectedPreviewListing.co2SavingsKg} kg CO₂</span>
                </div>

                {/* AI Screening Audit */}
                <div className="p-3.5 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[#90cbb7] font-semibold">AI Automated Screening:</span>
                    <span className={`font-bold px-2 py-0.5 rounded text-[10px] ${
                      selectedPreviewListing.aiModerationStatus === 'quarantined'
                        ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                        : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`}>
                      {selectedPreviewListing.aiModerationStatus?.toUpperCase() || 'CLEAN'} ({selectedPreviewListing.aiConfidenceScore || 99}%)
                    </span>
                  </div>
                  <p className="text-[11px] text-[#90cbb7]/80">
                    Image and text passed through Gemini Vision contraband classifier.
                  </p>
                </div>

                {/* Seller Mini Card */}
                <div className="p-3.5 rounded-2xl bg-[#152a23] border border-[#22493c] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-500/20 text-brand-400 flex items-center justify-center font-bold">
                      {selectedPreviewListing.sellerName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-white">{selectedPreviewListing.sellerName}</p>
                      <p className="text-[10px] text-[#90cbb7]">Rating: ★ {selectedPreviewListing.sellerRating || 4.8} • {selectedPreviewListing.location}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const user = participants.find(p => p.uid === selectedPreviewListing.sellerId);
                      if (user) {
                        handleOpenBanModal(user);
                      } else {
                        alert('Seller profile ID: ' + selectedPreviewListing.sellerId);
                      }
                    }}
                    className="px-2.5 py-1.5 rounded-xl border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-[10px] font-bold transition-all flex items-center gap-1"
                  >
                    <Ban className="w-3 h-3" />
                    <span>Moderate Seller</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-1.5">
              <h4 className="font-bold text-white text-xs uppercase tracking-wider">Product Description:</h4>
              <p className="text-xs text-[#90cbb7] leading-relaxed">
                {selectedPreviewListing.description}
              </p>
            </div>

            {/* Flag / Delist details */}
            {selectedPreviewListing.flagReason && (
              <div className="p-3.5 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300">
                <strong className="block text-xs font-bold text-rose-200 mb-1">Active Violation Flag:</strong>
                <span>{selectedPreviewListing.flagReason}</span>
              </div>
            )}

            {selectedPreviewListing.delistedReason && (
              <div className="p-3.5 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-300">
                <strong className="block text-xs font-bold text-amber-200 mb-1">Delist Notice:</strong>
                <span>{selectedPreviewListing.delistedReason}</span>
              </div>
            )}

            {/* Modal Actions Footer */}
            <div className="pt-4 border-t border-[#22493c] flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => handleDeleteListing(selectedPreviewListing.id)}
                className="px-4 py-2 rounded-xl bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 text-xs font-bold transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Permanently</span>
              </button>

              <div className="flex items-center gap-2.5">
                {/* Enlist if delisted */}
                {selectedPreviewListing.status === 'delisted' && (
                  <button
                    type="button"
                    onClick={() => handleEnlistListing(selectedPreviewListing.id)}
                    className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-forest-void text-xs font-extrabold shadow-glow-sm transition-all flex items-center gap-2"
                  >
                    <ArrowUpCircle className="w-4 h-4 stroke-[2.5]" />
                    <span>Enlist Item (Publish to Mobile App)</span>
                  </button>
                )}

                {/* Delist if active */}
                {selectedPreviewListing.status === 'active' && (
                  <button
                    type="button"
                    onClick={() => handleDelistListing(selectedPreviewListing.id)}
                    className="px-4 py-2 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-xs font-bold transition-colors flex items-center gap-1.5"
                  >
                    <EyeOff className="w-4 h-4" />
                    <span>Delist Listing</span>
                  </button>
                )}

                {/* Unflag if flagged */}
                {selectedPreviewListing.status === 'flagged' ? (
                  <button
                    type="button"
                    onClick={() => handleClearFlag(selectedPreviewListing.id)}
                    className="px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void text-xs font-extrabold shadow-glow-sm transition-all flex items-center gap-2"
                  >
                    <Check className="w-4 h-4 stroke-[3]" />
                    <span>Approve & Clear Flag</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleOpenFlagModal(selectedPreviewListing)}
                    className="px-4 py-2 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 text-xs font-bold transition-colors flex items-center gap-1.5"
                  >
                    <Flag className="w-4 h-4" />
                    <span>Manually Flag</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* 11. MODAL: MANUAL FLAG MODAL */}
      {/* ========================================================================= */}
      {isFlagModalOpen && listingToFlag && (
        <Modal
          isOpen={isFlagModalOpen}
          onClose={() => setIsFlagModalOpen(false)}
          title="Flag Marketplace Listing for Moderation"
          subtitle={`Flag item: ${listingToFlag.title} (${listingToFlag.id})`}
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Violation Category <span className="text-brand-400">*</span>
              </label>
              <select
                value={flagReasonCategory}
                onChange={(e) => setFlagReasonCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500 cursor-pointer"
              >
                <option value="Counterfeit / Replica Goods">Counterfeit / Replica Goods</option>
                <option value="Prohibited or Stolen Property">Prohibited or Stolen Property</option>
                <option value="Misleading Condition or Price">Misleading Condition or Bait-and-Switch Price</option>
                <option value="Inappropriate / Adult / Violent Imagery">Inappropriate / Adult / Violent Imagery</option>
                <option value="Suspicious Contact / Off-Platform Solicitation">Suspicious Contact / Off-Platform Solicitation</option>
                <option value="Suspected Fake / Spam Listing">Suspected Fake / Spam Listing</option>
                <option value="Other Policy Violation">Other Policy Violation</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Staff Moderation Notes (Optional):
              </label>
              <textarea
                rows={3}
                value={flagReasonDetails}
                onChange={(e) => setFlagReasonDetails(e.target.value)}
                placeholder="Specific context for why this listing was flagged (e.g. Serial number mismatch, replica engravings detected)..."
                className="w-full p-3 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
              />
            </div>

            <div className="pt-3 border-t border-[#22493c] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsFlagModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmitFlag}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-rose-500 hover:bg-rose-400 text-white shadow-glow-sm transition-all flex items-center gap-1.5"
              >
                <Flag className="w-3.5 h-3.5" />
                <span>Confirm & Flag Listing</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* 12. MODAL: BAN USER MODAL */}
      {/* ========================================================================= */}
      {isBanModalOpen && userToBan && (
        <Modal
          isOpen={isBanModalOpen}
          onClose={() => setIsBanModalOpen(false)}
          title="Ban / Suspend Marketplace Participant"
          subtitle={`Take moderation action against ${userToBan.name} (${userToBan.email})`}
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            {/* User Details Pill */}
            <div className="p-3.5 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center justify-between">
              <div>
                <p className="font-bold text-white text-xs">{userToBan.name}</p>
                <p className="text-[11px] text-[#90cbb7]">{userToBan.email} • {userToBan.phone}</p>
                <p className="text-[10px] text-[#90cbb7]/60 font-mono">UID: {userToBan.uid}</p>
              </div>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-brand-500/20 text-brand-400">
                {userToBan.role}
              </span>
            </div>

            {/* Ban Type */}
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Suspension Scope <span className="text-brand-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setBanType('temporary')}
                  className={`p-3 rounded-xl border text-center font-bold text-xs transition-all ${
                    banType === 'temporary'
                      ? 'bg-amber-500/20 text-amber-300 border-amber-500'
                      : 'bg-[#10231d] border-[#22493c] text-[#90cbb7]'
                  }`}
                >
                  Temporary Suspension
                </button>

                <button
                  type="button"
                  onClick={() => setBanType('permanent')}
                  className={`p-3 rounded-xl border text-center font-bold text-xs transition-all ${
                    banType === 'permanent'
                      ? 'bg-rose-500/20 text-rose-300 border-rose-500'
                      : 'bg-[#10231d] border-[#22493c] text-[#90cbb7]'
                  }`}
                >
                  Permanent Ban
                </button>
              </div>
            </div>

            {/* If Temporary: Duration */}
            {banType === 'temporary' && (
              <div>
                <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                  Suspension Duration (Days):
                </label>
                <select
                  value={banDurationDays}
                  onChange={(e) => setBanDurationDays(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500 cursor-pointer"
                >
                  <option value={3}>3 Days</option>
                  <option value={7}>7 Days (Standard)</option>
                  <option value={14}>14 Days (Severe)</option>
                  <option value={30}>30 Days (Probation)</option>
                </select>
              </div>
            )}

            {/* Reason Category */}
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Violation Reason <span className="text-brand-400">*</span>
              </label>
              <select
                value={banReasonCategory}
                onChange={(e) => setBanReasonCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500 cursor-pointer"
              >
                <option value="Counterfeit Goods Listing">Counterfeit Goods Listing</option>
                <option value="Escrow Payment Fraud">Escrow Payment Fraud / Fake Slips</option>
                <option value="Off-Platform Settlement Solicitation">Off-Platform Settlement Solicitation</option>
                <option value="Harassment, Abuse or Threatening Behavior">Harassment, Abuse or Threatening Behavior</option>
                <option value="Prohibited Contraband or Stolen Property">Prohibited Contraband or Stolen Property</option>
                <option value="Repeated Delivery Defaults">Repeated Delivery Defaults</option>
                <option value="Other Severe Guideline Breach">Other Severe Guideline Breach</option>
              </select>
            </div>

            {/* Staff Notes */}
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Staff Internal Reference & Notes:
              </label>
              <textarea
                rows={3}
                value={banStaffNotes}
                onChange={(e) => setBanStaffNotes(e.target.value)}
                placeholder="Include incident tickets, police references, or dispute tribunal IDs..."
                className="w-full p-3 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
              />
            </div>

            {/* Auto-delist checkbox */}
            <div className="flex items-center gap-2 p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
              <input
                type="checkbox"
                id="autoDelistCheck"
                checked={banAutoDelistListings}
                onChange={(e) => setBanAutoDelistListings(e.target.checked)}
                className="w-4 h-4 accent-[#0df2a6] rounded cursor-pointer"
              />
              <label htmlFor="autoDelistCheck" className="text-xs text-white font-medium cursor-pointer">
                Automatically delist all active marketplace listings by this seller
              </label>
            </div>

            <div className="pt-3 border-t border-[#22493c] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsBanModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[#90cbb7] hover:text-white"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmBan}
                className="px-5 py-2.5 rounded-xl text-xs font-extrabold bg-rose-500 hover:bg-rose-400 text-white shadow-glow-sm transition-all flex items-center gap-2"
              >
                <Ban className="w-4 h-4" />
                <span>Execute Ban & Suspend Account</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* 13. MODAL: USER REPORTS DOSSIER */}
      {/* ========================================================================= */}
      {isReportsModalOpen && userForReports && (
        <Modal
          isOpen={isReportsModalOpen}
          onClose={() => setIsReportsModalOpen(false)}
          title="User Incident Reports Dossier"
          subtitle={`Complaints filed against ${userForReports.name} (${userForReports.email})`}
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <span>Total Reports Logged: <strong className="text-white">{userForReports.reports?.length || 0}</strong></span>
              </div>
              <span className="text-[10px] font-bold text-rose-400 uppercase">
                Trust Score: {userForReports.sellerTrustScore}%
              </span>
            </div>

            {/* List of Report Incidents */}
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {(userForReports.reports || []).map((rep) => (
                <div key={rep.id} className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-xs flex items-center gap-1.5">
                      <Flag className="w-3.5 h-3.5 text-rose-400" />
                      {rep.reason}
                    </span>
                    <span className="text-[10px] text-[#90cbb7]/70">{rep.timestamp}</span>
                  </div>

                  <p className="text-xs text-[#90cbb7] leading-relaxed">
                    "{rep.description}"
                  </p>

                  <div className="pt-2 border-t border-[#22493c] flex items-center justify-between text-[10px] text-[#90cbb7]/70">
                    <span>Filed by: <strong className="text-white">{rep.reporterName}</strong> ({rep.reporterId})</span>
                    {rep.relatedListingId && (
                      <span className="font-mono text-brand-400">Item: {rep.relatedListingId}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-[#22493c] flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsReportsModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-[#90cbb7] hover:text-white"
              >
                Close Dossier
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsReportsModalOpen(false);
                  handleOpenBanModal(userForReports);
                }}
                className="px-5 py-2.5 rounded-xl text-xs font-bold bg-rose-500 hover:bg-rose-400 text-white shadow-glow-sm transition-all flex items-center gap-2"
              >
                <Ban className="w-4 h-4" />
                <span>Ban User for Reported Violations</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* 14. MODAL: APPEAL REVIEW & UNBAN */}
      {/* ========================================================================= */}
      {isAppealModalOpen && userForAppeal && (
        <Modal
          isOpen={isAppealModalOpen}
          onClose={() => setIsAppealModalOpen(false)}
          title="Review Account Suspension Appeal"
          subtitle={`Appeal submitted by ${userForAppeal.name} (${userForAppeal.email})`}
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            {/* Original Ban Context */}
            <div className="p-4 rounded-2xl bg-[#10231d] border border-rose-500/30 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-rose-400 font-bold uppercase text-[10px] tracking-wider">
                  Original Ban Verdict ({userForAppeal.banInfo?.type?.toUpperCase() || 'SUSPENDED'})
                </span>
                <span className="text-[10px] text-[#90cbb7]">Date: {userForAppeal.banInfo?.bannedAt}</span>
              </div>
              <p className="font-bold text-white text-xs">{userForAppeal.banInfo?.reasonCategory}</p>
              <p className="text-xs text-[#90cbb7]">{userForAppeal.banInfo?.staffNotes}</p>
            </div>

            {/* Submitted Appeal Letter */}
            <div className="p-4 rounded-2xl bg-[#152a23] border border-brand-500/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-brand-400 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" />
                  User Appeal Statement
                </span>
                <span className="text-[10px] text-[#90cbb7]">{userForAppeal.appealInfo?.submittedAt}</span>
              </div>
              <p className="font-bold text-white text-xs">{userForAppeal.appealInfo?.reason}</p>
              <div className="p-3 rounded-xl bg-[#10231d] border border-[#22493c] text-white leading-relaxed text-xs">
                "{userForAppeal.appealInfo?.userStatement}"
              </div>
            </div>

            {/* Staff Resolution Notes */}
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] mb-1.5">
                Staff Appeal Resolution Note (Recorded to Security Audit):
              </label>
              <textarea
                rows={2}
                value={appealReviewNotes}
                onChange={(e) => setAppealReviewNotes(e.target.value)}
                placeholder="e.g. Reviewed user explanation, prohibited inventory removed, verified KYC compliance..."
                className="w-full p-3 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
              />
            </div>

            {/* Decision Buttons */}
            <div className="pt-3 border-t border-[#22493c] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => handleRejectAppeal(userForAppeal)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 border border-rose-500/30 transition-all flex items-center gap-1.5"
              >
                <X className="w-4 h-4" />
                <span>Reject Appeal & Maintain Ban</span>
              </button>

              <button
                type="button"
                onClick={() => handleApproveAppealAndUnban(userForAppeal, true)}
                className="px-5 py-2.5 rounded-xl text-xs font-extrabold bg-emerald-500 hover:bg-emerald-400 text-forest-void shadow-glow-sm transition-all flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4 stroke-[2.5]" />
                <span>Approve Appeal & Unban User</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ========================================================================= */}
      {/* 15. MODAL: ADD CATEGORY MODAL */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isAddCategoryOpen}
        onClose={() => setIsAddCategoryOpen(false)}
        title="Add Marketplace Catalog Category"
        subtitle="Create a new item category featured on the ReUseIt mobile app"
        maxWidth="md"
      >
        <div className="space-y-4 text-xs">
          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Category Name:</label>
            <input
              type="text"
              value={categoryFormName}
              onChange={(e) => {
                setCategoryFormName(e.target.value);
                if (!categoryFormSlug) {
                  setCategoryFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                }
              }}
              placeholder="e.g. Solar & Renewable Energy"
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">URL Slug:</label>
            <input
              type="text"
              value={categoryFormSlug}
              onChange={(e) => setCategoryFormSlug(e.target.value)}
              placeholder="e.g. solar-renewable-energy"
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none font-mono text-xs focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Cover Image URL:</label>
            <input
              type="url"
              value={categoryFormImageUrl}
              onChange={(e) => setCategoryFormImageUrl(e.target.value)}
              placeholder="https://images.unsplash.com/..."
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Description:</label>
            <textarea
              rows={2}
              value={categoryFormDescription}
              onChange={(e) => setCategoryFormDescription(e.target.value)}
              placeholder="Brief description of items in this category..."
              className="w-full p-3 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
            <div>
              <span className="font-bold text-white block">Feature on Mobile App Home Carousel</span>
              <span className="text-[11px] text-[#90cbb7]">Places category in top round carousel on mobile app</span>
            </div>
            <input
              type="checkbox"
              checked={categoryFormFeatured}
              onChange={(e) => setCategoryFormFeatured(e.target.checked)}
              className="w-4 h-4 accent-[#0df2a6] rounded cursor-pointer"
            />
          </div>

          <div className="pt-4 border-t border-[#22493c] flex justify-end gap-3">
            <button
              onClick={() => setIsAddCategoryOpen(false)}
              className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveNewCategory}
              disabled={!categoryFormName.trim()}
              className="px-6 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
            >
              Save Category
            </button>
          </div>
        </div>
      </Modal>

      {/* ========================================================================= */}
      {/* 16. MODAL: EDIT CATEGORY MODAL */}
      {/* ========================================================================= */}
      <Modal
        isOpen={isEditCategoryOpen}
        onClose={() => setIsEditCategoryOpen(false)}
        title="Edit Marketplace Category"
        subtitle={`Update settings for ${editingCategory?.name}`}
        maxWidth="md"
      >
        <div className="space-y-4 text-xs">
          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Category Name:</label>
            <input
              type="text"
              value={categoryFormName}
              onChange={(e) => setCategoryFormName(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">URL Slug:</label>
            <input
              type="text"
              value={categoryFormSlug}
              onChange={(e) => setCategoryFormSlug(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none font-mono text-xs focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Cover Image URL:</label>
            <input
              type="url"
              value={categoryFormImageUrl}
              onChange={(e) => setCategoryFormImageUrl(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#90cbb7] mb-1">Description:</label>
            <textarea
              rows={2}
              value={categoryFormDescription}
              onChange={(e) => setCategoryFormDescription(e.target.value)}
              className="w-full p-3 rounded-xl border bg-[#10231d] border-[#22493c] text-white outline-none focus:border-brand-500"
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-[#10231d] border border-[#22493c]">
            <div>
              <span className="font-bold text-white block">Feature on Mobile App Home Carousel</span>
              <span className="text-[11px] text-[#90cbb7]">Places category in top round carousel on mobile app</span>
            </div>
            <input
              type="checkbox"
              checked={categoryFormFeatured}
              onChange={(e) => setCategoryFormFeatured(e.target.checked)}
              className="w-4 h-4 accent-[#0df2a6] rounded cursor-pointer"
            />
          </div>

          <div className="pt-4 border-t border-[#22493c] flex justify-end gap-3">
            <button
              onClick={() => setIsEditCategoryOpen(false)}
              className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateCategory}
              disabled={!categoryFormName.trim()}
              className="px-6 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
            >
              Update Category
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
